import { FC, ReactNode, useMemo } from 'react';
import { Tag, type TagProps } from 'antd';
import cn from 'classnames';
import styles from './statusTag.module.scss';

export type StatusTagProps = TagProps & {
    value?: ReactNode;
};

const DEFAULT_BACKGROUND = '#dadada';
const DARK_TEXT = '#111827';
const LIGHT_TEXT = '#ffffff';

const parseRgb = (input?: string | number) => {
    if (!input || typeof input !== 'string') return null;
    const value = input.trim();

    if (value.startsWith('#')) {
        const hex = value.slice(1);
        if (hex.length === 3) {
            const r = parseInt(hex[0] + hex[0], 16);
            const g = parseInt(hex[1] + hex[1], 16);
            const b = parseInt(hex[2] + hex[2], 16);
            return Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)
                ? null
                : ([r, g, b] as const);
        }
        if (hex.length === 6) {
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)
                ? null
                : ([r, g, b] as const);
        }
        return null;
    }

    const rgbMatch =
        /^rgba?\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\)$/i.exec(
            value
        );

    if (rgbMatch) {
        const r = Number(rgbMatch[1]);
        const g = Number(rgbMatch[2]);
        const b = Number(rgbMatch[3]);

        if (
            Number.isFinite(r) &&
            Number.isFinite(g) &&
            Number.isFinite(b) &&
            r <= 255 &&
            g <= 255 &&
            b <= 255
        ) {
            return [r, g, b] as const;
        }
    }

    return null;
};

const getReadableTextColor = (background?: string) => {
    const rgb = parseRgb(background);
    if (!rgb) {
        return DARK_TEXT;
    }

    const [r, g, b] = rgb;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? DARK_TEXT : LIGHT_TEXT;
};

export const StatusTagField: FC<StatusTagProps> = ({
    value,
    children,
    color,
    className,
    style,
    ...rest
}) => {
    const content = children ?? value;

    const { backgroundColor, textColor } = useMemo(() => {
        const background =
            color ?? (style?.backgroundColor as string | undefined);
        const resolvedBackground = background ?? DEFAULT_BACKGROUND;
        const resolvedTextColor =
            (style?.color as string | undefined) ??
            getReadableTextColor(resolvedBackground);

        return {
            backgroundColor: resolvedBackground,
            textColor: resolvedTextColor,
        };
    }, [color, style?.backgroundColor, style?.color]);

    return (
        <Tag
            {...rest}
            color={undefined}
            className={cn(styles.tag, className)}
            style={{
                ...style,
                backgroundColor,
                color: textColor,
            }}
        >
            {content}
        </Tag>
    );
};

export default StatusTagField;
